import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import TooltipComponent from "../reusables/TooltipComponent";
import RouteSelect from "../reusables/RouteSelect";
import { formatPhoneNumber } from "../utils/formatnumber";
import { Divider } from "@mui/material";

/**
 * Pure UI component for Forward action
 * All logic is handled by useForwardAction hook
 */
const DEFAULT_CALLER_ID_PREF = "caller";

const DEFAULT_CALLER_ID_OPTIONS = [
  { value: "caller", label: "Show Original Caller's Number" },
  { value: "twilio", label: "Show System Number" },
];

const ForwardUI = ({
  numberValue,
  callerIdPref,
  handleNumberChange,
  handleCallerIdChange,
  containerClass,
  inputClass,
  headerSize,
  showDivider,
  setOpenConfigureSlotModal,
  channels = [],
  callerIdOptions = null,
  hasValidationError = false,
  addDestinationOption,
  /** When provided, used for destination select (and should resolve ch_id -> phone before saving). Use this so primaryNumber/offHoursNumber store phone, not channel_id. */
  onDestinationChange,
}) => {
  const effectiveCallerIdOptions = callerIdOptions ?? DEFAULT_CALLER_ID_OPTIONS;
  const defaultCallerId = effectiveCallerIdOptions[0]?.value ?? DEFAULT_CALLER_ID_PREF;
  const hasNonDefaultAdvanced = callerIdPref !== defaultCallerId;
  const [advancedOpen, setAdvancedOpen] = useState(() => hasNonDefaultAdvanced);

  const destinationOptions = useMemo(() => {
    if (!channels) return [];
    return channels
      .filter((item) => item.M.type.S === "forward_number")
      .map((item) => {
        const chId = item.M.ch_id?.S ?? item.ch_id ?? item.M.value?.S;
        const formattedNumber = formatPhoneNumber(item.M.value?.S ?? item.M.value ?? "");
        const label = item.M.label?.S ?? item.label ?? "";
        return { value: chId, label: label ? `${label} - ${formattedNumber}` : formattedNumber };
      });
  }, [channels]);

  // Resolve display value: use numberValue if it matches an option (e.g. channel id); else resolve phone to ch_id so label shows
  const destinationValue = useMemo(() => {
    if (!numberValue) return numberValue;
    const hasMatch = destinationOptions.some((o) => o.value === numberValue);
    if (hasMatch) return numberValue;
    const byPhone = channels?.find(
      (c) => c.M?.type?.S === "forward_number" && (c.M.value?.S ?? c.value) === numberValue
    );
    if (byPhone) return byPhone.M?.ch_id?.S ?? byPhone.ch_id ?? numberValue;
    return numberValue;
  }, [numberValue, destinationOptions, channels]);

  // Keep advanced open whenever any advanced value is non-default (e.g. when coming back to this route)
  useEffect(() => {
    if (hasNonDefaultAdvanced) {
      setAdvancedOpen(true);
    }
  }, [hasNonDefaultAdvanced]);

  return (
    <div>
      <div className={`${containerClass}${hasValidationError ? " !bg-red-50/80" : ""}`}>
        {/* Header */}
        <div className="flex flex-nowrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center min-w-0 flex-1">
            <ArrowRight className="text-green-500 shrink-0 mr-2" />
            <div className="min-w-0">
              <h2 className={`${headerSize} text-gray-800`}>
                Forward Call
              </h2>
              <div className="text-[13px]">Send the call to another number</div>
            </div>
          </div>
          {setOpenConfigureSlotModal && (
            <button
              className="shrink-0 whitespace-nowrap text-sm text-blue-600 font-medium hover:underline text-[13px]"
              onClick={() => setOpenConfigureSlotModal(true)}
            >
              ↻ Change
            </button>
          )}
        </div>
        {showDivider && <Divider />}

        {/* Destination */}
        <div className="mb-4">
          <div className="flex items-center gap-1 mb-1">
            <span className="text-sm font-medium text-gray-700">
              Destination
            </span>
            <TooltipComponent message={"Select the number to forward the call to"} />
          </div>
          <RouteSelect
            value={destinationValue}
            onChange={onDestinationChange ?? handleNumberChange}
            options={destinationOptions}
            placeholder="Select a destination"
            addOption={addDestinationOption}
          />
        </div>

        {/* Advanced Settings (collapsible) */}
        <div>
          <button
            type="button"
            onClick={() => setAdvancedOpen((prev) => !prev)}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            {advancedOpen ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span>Advanced Settings</span>
          </button>
          {advancedOpen && (
            <div className="mt-3">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-sm font-medium text-gray-700">
                  Caller ID Setting
                </span>
                <TooltipComponent message={"Choose which caller ID to see when the call is forwarded"} />
              </div>
              <RouteSelect
                value={callerIdPref}
                onChange={handleCallerIdChange}
                options={effectiveCallerIdOptions}
                placeholder="Select preference"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForwardUI;
